import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { listConsultants, submitSelfCheck, WHATSAPP_NUMBER } from '../lib/api';
import { MapPin, Languages, Award, Sparkles, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const STORAGE_KEY = 'app_selfcheck';

export default function Consultants() {
  const { lang, t } = useLanguage();
  const navigate = useNavigate();
  const [consultants, setConsultants] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listConsultants()
      .then(setConsultants)
      .catch(() => setConsultants([]))
      .finally(() => setLoading(false));
  }, []);

  const choose = async (consultantId) => {
    // Update stored submission with preferred consultant
    const stored = localStorage.getItem(STORAGE_KEY);
    const data = stored ? JSON.parse(stored) : null;
    if (data) {
      data.preferred_consultant = consultantId;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      try {
        await submitSelfCheck({
          idempotency_key: data.idempotency_key,
          language: data.language || lang,
          user_role: data.user_role,
          postpartum_age: data.postpartum_age,
          birth_order: data.birth_order,
          location_type: data.location_type,
          location_text: data.location_text,
          feeding_method: data.feeding_method,
          main_concern: data.main_concern,
          red_flag_answers: data.red_flag_answers || {},
          module_answers: data.module_answers || {},
          risk_level: data.risk_level || 'green',
          result_type: data.result_type,
          preferred_consultant: consultantId,
        });
      } catch {}
    }
    toast.success(lang === 'zh' ? '已选择！正在打开 WhatsApp...' : 'Selected! Opening WhatsApp...');
    const consultant = consultants.find((c) => c.id === consultantId);
    const cName = consultant?.name || consultantId;
    const msg =
      lang === 'zh'
        ? `Hi Soo, 我想指定 ${cName} 作为我的泌乳顾问。`
        : `Hi Soo, I would like to choose ${cName} as my lactation consultant.`;
    setTimeout(() => {
      window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`, '_blank');
    }, 600);
  };

  const choosePlatform = async (mode) => {
    const stored = localStorage.getItem(STORAGE_KEY);
    const data = stored ? JSON.parse(stored) : null;
    if (data) {
      data.preferred_consultant = mode;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    }
    const msg = mode === 'platform_assign'
      ? (lang === 'zh' ? 'Hi Soo, 请帮我安排最快可服务的顾问。' : 'Hi Soo, please assign the fastest available consultant.')
      : (lang === 'zh' ? 'Hi Soo, 请你先帮我筛选合适的顾问。' : 'Hi Soo, please review my case first and assign accordingly.');
    toast.success(lang === 'zh' ? '已记录' : 'Saved');
    setTimeout(() => {
      window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`, '_blank');
    }, 600);
  };

  return (
    <div className="container-app py-8 sm:py-12 animate-fade-in-up" data-testid="consultants-page">
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-ink mb-3">
        {t('consultants_title')}
      </h1>
      <p className="text-ink-secondary mb-8">{t('consultants_subtitle')}</p>

      {/* Quick options */}
      <div className="grid sm:grid-cols-2 gap-3 mb-10">
        <button
          onClick={() => choosePlatform('platform_assign')}
          data-testid="opt-platform-assign"
          className="card-interactive p-5 text-left flex items-center gap-3"
        >
          <div className="w-10 h-10 rounded-xl bg-sage-light flex items-center justify-center flex-shrink-0">
            <Sparkles size={18} className="text-sage" />
          </div>
          <span className="font-medium text-ink">{t('consultant_let_platform')}</span>
        </button>
        <button
          onClick={() => choosePlatform('soo_review')}
          data-testid="opt-soo-review"
          className="card-interactive p-5 text-left flex items-center gap-3"
        >
          <div className="w-10 h-10 rounded-xl bg-terracotta-light flex items-center justify-center flex-shrink-0">
            <Award size={18} className="text-terracotta" />
          </div>
          <span className="font-medium text-ink">{t('consultant_let_soo_review')}</span>
        </button>
      </div>

      {loading ? (
        <p className="text-ink-tertiary text-center py-10">{t('loading')}</p>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {consultants.map((c) => (
            <div
              key={c.id}
              className={`card-base p-6 ${c.is_founder ? 'ring-2 ring-terracotta/30' : ''}`}
              data-testid={`consultant-${c.id}`}
            >
              <div className="flex gap-4 mb-4">
                {c.image_url ? (
                  <img
                    src={c.image_url}
                    alt={c.name}
                    loading="lazy"
                    className="w-20 h-20 rounded-2xl object-cover border-2 border-terracotta-light flex-shrink-0"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-sage-light to-terracotta-light/60 border-2 border-terracotta-light flex-shrink-0 flex items-center justify-center font-heading text-2xl font-semibold text-terracotta">
                    {(c.name || '?').slice(0, 1)}
                  </div>
                )}
                <div className="min-w-0">
                  <h3 className="font-heading text-lg font-semibold text-ink truncate">{c.name}</h3>
                  <p className="text-xs text-ink-tertiary leading-snug">
                    {lang === 'zh' ? c.role_zh : c.role_en}
                  </p>
                  {c.is_founder && (
                    <span className="inline-block mt-2 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-terracotta-light text-terracotta">
                      {lang === 'zh' ? '创办人' : 'Founder'}
                    </span>
                  )}
                </div>
              </div>

              <p className="text-sm text-ink-secondary leading-relaxed mb-4">
                {lang === 'zh' ? c.bio_zh : c.bio_en}
              </p>

              <div className="space-y-2 text-xs text-ink-tertiary mb-5">
                <div className="flex items-start gap-2">
                  <Award size={12} className="mt-0.5 flex-shrink-0 text-terracotta" />
                  <span>{(lang === 'zh' ? c.specialties_zh : c.specialties_en).join(' · ')}</span>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin size={12} className="mt-0.5 flex-shrink-0 text-sage" />
                  <span>{c.service_areas.join(', ')}</span>
                </div>
                <div className="flex items-start gap-2">
                  <Languages size={12} className="mt-0.5 flex-shrink-0 text-ink-tertiary" />
                  <span>{c.languages.join(' / ')}</span>
                </div>
              </div>

              <button
                onClick={() => choose(c.id)}
                data-testid={`choose-${c.id}`}
                className="btn-primary w-full"
              >
                <MessageCircle size={14} className="mr-2" />
                {c.is_founder ? t('consultant_choose_soo') : t('consultant_choose_this')}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
